import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import store from 'store'
import constants from 'config/constants'
import * as R from 'ramda'

dayjs.extend(customParseFormat)

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,6})?(Z|[+-]\d{2}:\d{2})$/
export function getDefaultDateRange() {
  return getDateRange({ days: 90 })
}

export function getDateRange(rangeObject) {
  const { days, weeks, months, quarters, years } = rangeObject
  if (days) {
    return getDateRangeFromPreset(days, 'day')
  } else if (weeks) {
    return getDateRangeFromPreset(weeks, 'week')
  } else if (months) {
    return getDateRangeFromPreset(months, 'month')
  } else if (quarters) {
    return getDateRangeFromPreset(quarters, 'quarter')
  } else if (years) {
    return getDateRangeFromPreset(years, 'year')
  }
}

function getDateRangeFromPreset(range, unit) {
  let startValue, endValue
  if (Array.isArray(range)) {
    ;[startValue, endValue] = range
  } else {
    startValue = range
    endValue = 0
  }

  const endDate = getDayjsToday().subtract(endValue, unit).endOf(unit).utc().format()
  const startDate = getDayjsToday()
    .subtract(startValue - 1, unit)
    .startOf(unit)
    .utc()
    .format()
  return [startDate, endDate]
}

export function getDayjsToday() {
  const workspaceTimezone = getWorkspaceTimezone()
  return dayjs.utc().tz(workspaceTimezone)
}

export function getCurrentTimezoneOffset() {
  return -new Date().getTimezoneOffset()
}

export function getDefaultHoursSchedule() {
  const workspaceTimezone = getWorkspaceTimezone()

  return dayjs().tz(workspaceTimezone).add(1, 'hour').hour()
}

export function getWorkspaceTimezone() {
  const workspaceTimezone = store.get(constants.simplesatWorkspaceTimezone)
  const timezoneValue = workspaceTimezone?.value

  if (!timezoneValue || timezoneValue === 'default') {
    return dayjs.tz.guess()
  }
  return timezoneValue
}

export function getWorkspaceTimezoneLabel() {
  const workspaceTimezone = store.get(constants.simplesatWorkspaceTimezone)
  const timezoneLabel = workspaceTimezone?.label

  if (!timezoneLabel || workspaceTimezone?.label === 'Local Timezone') {
    return getLocalTimeZoneLabel()
  }
  return timezoneLabel
}

export function getLocalTimeZoneLabel() {
  const timeZone = dayjs.tz.guess()
  const now = new Date()

  // 1. Extract short offset (e.g. "GMT-3")
  const [{ value: rawOffset }] = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    timeZoneName: 'shortOffset',
  })
    .formatToParts(now)
    .filter((part) => part.type === 'timeZoneName')

  const offset = rawOffset.replace(/GMT([+-]?)(\d{1,2})(?::(\d{2}))?/, (_, sign, h, m = '00') => {
    const hours = h.padStart(2, '0')
    return `UTC${sign}${hours}:${m}`
  })

  // 2. Extract long name (e.g. "Brasilia Standard Time")
  const [{ value: longName }] = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'long',
  })
    .formatToParts(now)
    .filter((part) => part.type === 'timeZoneName')

  return `(${offset}) ${longName}`
}

// The dates passed into the arguements here will be midnight based off the workspace timezone or local
// ex. dd:mm:yyyy:17:00:00 -  dd:mm:yyyy:16:59:59
export const convertDateRangeToWorkspaceTimezoneAwareness = (startDate, endDate) => {
  if (R.isNil(startDate) || R.isNil(endDate)) {
    return [null, null]
  }
  const workspaceTimezone = getWorkspaceTimezone()
  return [
    dayjs(startDate).tz(workspaceTimezone).startOf('day').utc().format(),
    dayjs(endDate).tz(workspaceTimezone).endOf('day').utc().format(),
  ]
}

export const toDayjs = (dateTimeString) => {
  const workspaceTimezone = getWorkspaceTimezone()
  return dayjs(dateTimeString).tz(workspaceTimezone)
}

// We do not want to convert timezone in this function because it is later converted by toDayjs
// https://github.com/simplesat/simplesat/blob/f2c5e74404b50bfcf373aac9cbdd64a45d234f2d/dashboard/src/organisms/Dashboards/CustomDashboardFilterGroup/DateRangePicker/index.js#L71
export const convertAntdDateRangeToUtcDatetimeFormat = (antdDateRange) => {
  const [startDate, endDate] = antdDateRange

  return [startDate.startOf('day').utc().format(), endDate.endOf('day').utc().format()]
}

export const getDisabledDateAfterToday = (date) => {
  const workspaceTimezone = getWorkspaceTimezone()

  return date > dayjs().tz(workspaceTimezone).endOf('day')
}

/**
 * Returns the workspace's week-start setting from localStorage. Returns 'sunday'
 * only when the stored value is exactly 'sunday'; everything else is 'monday'.
 * No feature-flag check — the UI switch that writes 'sunday' is itself
 * flag-gated, so a 'sunday' value in localStorage already implies the user
 * opted in.
 *
 * @returns {'monday'|'sunday'}
 */
export function getWorkspaceWeekStartDay() {
  return store.get(constants.simplesatWorkspaceWeekStartDay) === 'sunday' ? 'sunday' : 'monday'
}

/**
 * Computes the start of the week for a given dayjs date using day-of-week arithmetic.
 * Does NOT use dayjs.startOf('week') to avoid locale lock.
 *
 * @param {import('dayjs').Dayjs} dayjsDate
 * @param {'monday'|'sunday'} [weekStartDay] - when omitted, reads from localStorage via getWorkspaceWeekStartDay()
 * @returns {import('dayjs').Dayjs}
 */
export function getWorkspaceAwareStartOfWeek(dayjsDate, weekStartDay) {
  const resolvedWeekStart = weekStartDay !== undefined ? weekStartDay : getWorkspaceWeekStartDay()
  if (resolvedWeekStart === 'sunday') {
    return dayjsDate.subtract(dayjsDate.day(), 'day').startOf('day')
  }
  // monday: subtract (day + 6) % 7 days
  return dayjsDate.subtract((dayjsDate.day() + 6) % 7, 'day').startOf('day')
}

/**
 * Computes the end of the week: 6 days after the start of the week.
 *
 * @param {import('dayjs').Dayjs} dayjsDate
 * @param {'monday'|'sunday'} [weekStartDay]
 * @returns {import('dayjs').Dayjs}
 */
export function getWorkspaceAwareEndOfWeek(dayjsDate, weekStartDay) {
  return getWorkspaceAwareStartOfWeek(dayjsDate, weekStartDay).add(6, 'day').endOf('day')
}

/**
 * Returns true when `value` is a valid ISO 8601 date or datetime string.
 * Uses dayjs `customParseFormat` in strict mode — rejects permissive
 * `new Date()` parses such as `123` or `03/15/2024`.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isIsoDateTime(value) {
  const str = String(value).trim()
  if (!str) {
    return false
  }
  if (ISO_DATE_RE.test(str)) {
    return isCalendarDateValid(str)
  }
  if (ISO_DATETIME_RE.test(str)) {
    if (!isCalendarDateValid(str.slice(0, 10))) {
      return false
    }
    const hour = parseInt(str.slice(11, 13), 10)
    const minute = parseInt(str.slice(14, 16), 10)
    const second = parseInt(str.slice(17, 19), 10)
    return hour <= 23 && minute <= 59 && second <= 59
  }
  return false
}

function isCalendarDateValid(dateStr) {
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) {
    return false
  }
  const [y, m, day] = dateStr.split('-').map(Number)
  return d.getUTCFullYear() === y && d.getUTCMonth() + 1 === m && d.getUTCDate() === day
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/libs/date/index.js